import { Controller } from "@hotwired/stimulus"

// facilities--location-filter — the site management page's Site → Building →
// Floor → Zone bar. Picking a level clears every level below it, then submits.
// The form targets the turbo-frame the bar itself lives in, so the server
// answers with each select narrowed under the new pick; this controller holds
// no option lists of its own.
//
// WHY CLEAR BELOW. The server fills a missing ancestor from a deeper pick
// (`?zone_id=` alone is a whole place — a link from elsewhere carries just
// that). So clearing the building while a zone is still selected would come
// straight back with the zone's building filled in again. Clearing what sits
// below the level the user touched is how "All buildings" means all buildings.
//
// WHY NOT facilities--filter-cascade. That one narrows the lists in the
// browser and keeps a child whose value survives the narrowing — right for a
// drawer applied with a button, wrong here, where the request IS the
// narrowing and a surviving child would pin the parent the user just cleared.
//
// Contract: each level is a wrapper
//   data-facilities--location-filter-target="level"
// holding one <select>, in order from the top. The form carries
//   data-action="change->facilities--location-filter#pick
//                formdata->facilities--location-filter#prune".
export default class extends Controller {
  static targets = ["level"]

  pick(event) {
    // Tom Select also dispatches change from its own control input; only the
    // <select> it drives is a pick.
    if (!(event.target instanceof HTMLSelectElement)) return

    const index = this.levelTargets.findIndex((level) =>
      level.contains(event.target),
    )
    if (index < 0) return

    this.levelTargets.slice(index + 1).forEach((level) => {
      const select = level.querySelector("select")
      if (!select) return
      // Silently: a change event here would re-enter this action. To the
      // "All X" row rather than cleared, so the control reads "All buildings"
      // while the frame loads instead of going blank.
      select.tomselect?.setValue("", true)
      select.value = ""
    })

    this.element.requestSubmit()
  }

  // Drops the levels left at "All" from the submission, so the URL the frame
  // advances to names only the place picked — a link to share, not a row of
  // empty `building_id=&floor_level=&zone_id=`. Turbo builds its request body
  // with `new FormData(form)`, which is what fires `formdata`.
  prune(event) {
    for (const [key, value] of [...event.formData.entries()]) {
      if (value === "") event.formData.delete(key)
    }
  }
}
